import {sendEvent} from '@github-ui/hydro-analytics'
import {Link} from '@github-ui/react-router'
import {FileDiffIcon, GitPullRequestIcon} from '@primer/octicons-react'
import {Button, ButtonGroup, IconButton} from '@primer/react'
import {useEffect} from 'react'

import {useCreatePullRequestForTask} from '../../hooks/use-create-pull-request-for-task'

interface PullRequestButtonProps {
  pullUrl?: string
  repoNwo: string
  baseRef?: string
  headRef?: string
  taskId?: string
  disabled?: boolean
  onError?: (message: string) => void
  /** When true and a pull request exists, renders a single icon-only, invisible button linking to
   * the PR (no label, no files-diff sibling). Used by StickyTaskChatHeader at narrow viewports to
   * keep the "View pull request" control in the header while saving horizontal space. */
  collapsed?: boolean
}

export function PullRequestButton({
  pullUrl,
  taskId,
  repoNwo,
  baseRef,
  headRef,
  disabled = false,
  onError,
  collapsed = false,
}: PullRequestButtonProps) {
  const {createPullRequest, isLoading} = useCreatePullRequestForTask()
  const pullRequestPath = pullUrl ? new URL(pullUrl, 'https://github.com').pathname : undefined

  const showCreatePrButton = !pullUrl && Boolean(baseRef) && Boolean(headRef)

  useEffect(() => {
    if (showCreatePrButton) {
      sendEvent('agent_sessions.task_view', {
        target: 'CREATE_PULL_REQUEST_BUTTON_RENDER',
        task_id: taskId,
      })
    }
  }, [showCreatePrButton, taskId])

  const handleViewPRClick = () => {
    sendEvent('agent_sessions.task_view', {
      target: 'VIEW_PULL_REQUEST_BUTTON',
      pull_url: pullUrl,
      task_id: taskId,
    })
  }

  const handleViewDiffClick = () => {
    sendEvent('agent_sessions.task_view', {
      target: 'VIEW_PULL_REQUEST_DIFF_BUTTON',
      pull_url: pullUrl,
      task_id: taskId,
    })
  }

  const handleCreatePRViaAPI = () => {
    if (!taskId) return

    sendEvent('agent_sessions.task_view', {
      target: 'CREATE_PULL_REQUEST_BUTTON',
      task_id: taskId,
    })

    createPullRequest(
      {repoNwo, taskId},
      {
        onSuccess: response => {
          window.location.href = `/${repoNwo}/pull/${response.number}`
        },
        onError: error => {
          onError?.(error instanceof Error ? error.message : 'Failed to create pull request')
        },
      },
    )
  }

  return pullUrl ? (
    collapsed ? (
      pullRequestPath ? (
        <IconButton
          as={Link}
          to={pullRequestPath}
          icon={GitPullRequestIcon}
          variant="invisible"
          size="small"
          onClick={handleViewPRClick}
          aria-label="View pull request"
        />
      ) : (
        <IconButton
          as="a"
          href={pullUrl}
          icon={GitPullRequestIcon}
          variant="invisible"
          size="small"
          onClick={handleViewPRClick}
          aria-label="View pull request"
        />
      )
    ) : (
      // The `f5` utility pins the font size to the default body size (14px) to prevent a Primer bug
      // where the divs wrapping ButtonGroup's children inherit the font size from the cascade
      // (PageHeader's 20px here), causing a misalignment between the Button and IconButton siblings.
      <ButtonGroup className="f5">
        {pullRequestPath ? (
          <Button
            as={Link}
            to={pullRequestPath}
            leadingVisual={GitPullRequestIcon}
            variant="primary"
            size="small"
            onClick={handleViewPRClick}
          >
            View pull request
          </Button>
        ) : (
          <Button
            as="a"
            href={pullUrl}
            leadingVisual={GitPullRequestIcon}
            variant="primary"
            size="small"
            onClick={handleViewPRClick}
          >
            View pull request
          </Button>
        )}
        {pullRequestPath ? (
          <IconButton
            as={Link}
            to={`${pullRequestPath}/files`}
            icon={FileDiffIcon}
            variant="primary"
            size="small"
            onClick={handleViewDiffClick}
            aria-label="View files changed"
          />
        ) : (
          <IconButton
            as="a"
            href={`${pullUrl}/files`}
            icon={FileDiffIcon}
            variant="primary"
            size="small"
            onClick={handleViewDiffClick}
            aria-label="View files changed"
          />
        )}
      </ButtonGroup>
    )
  ) : baseRef && headRef && taskId ? (
    <Button
      leadingVisual={GitPullRequestIcon}
      variant="primary"
      size="small"
      onClick={handleCreatePRViaAPI}
      loading={isLoading}
      disabled={disabled}
    >
      Create pull request
    </Button>
  ) : null
}
